/**
 * What the Embryo testing page says about a report, worked out once so the
 * page, its hero and its tests read the same answer: which state the report is
 * in, how many embryos landed in each result, every embryo from the cycle
 * (tested or not), and the reason behind each result.
 */
import type { Phase } from "@src/packages/cycles/hooks/useCycles";
import type {
  EmbryoGenomicResult,
  EmbryoReport,
} from "@src/packages/cycles/hooks/useEmbryoReport";
import { isInFrozenStorage } from "../frozenSpecimenAvailability";
import type { Specimen } from "../useSpecimens";
import { formatEmbryoGrade } from "../embryoGrade";
import { countEmbryoResults, type EmbryoResult } from "./embryoResult";
import {
  getEmbryoStorageState,
  type EmbryoStorageState,
} from "./embryoStorage";

export type EmbryoTestingState =
  /** The lab has not sent results for any embryo yet. */
  | "awaiting_results"
  /** The lab withdrew the report; its results must not be relied on. */
  | "withdrawn"
  /** No genetic testing was done or decided for this cycle. */
  | "not_tested"
  | "results";

export type EmbryoTestingOverview = {
  state: EmbryoTestingState;
  /** A partial report: more results may follow. */
  isPartial: boolean;
  tested: number;
  untested: number;
  counts: Record<EmbryoResult, number>;
  /** Results came back, and none of them is euploid or mosaic. */
  hasNoTransferableResult: boolean;
  /** Every tested embryo came back without a result. */
  hasOnlyNoResults: boolean;
};

const UNTESTED_REPORT_TYPES: ReadonlySet<EmbryoReport["testType"]> = new Set([
  "not_tested",
  "undecided",
  "clinic_pending",
]);

/** Test types that include an aneuploidy / chromosome result. */
const PGT_A_TEST_TYPES: ReadonlySet<EmbryoReport["testType"]> = new Set([
  "PGT-A",
  "PGT-A + PGT-M",
  "PGT-A + PGT-SR",
  "PGT-A + PGT-M + PGT-SR",
  "Whole Genome Sequencing",
]);

/** The current API gives a precise mosaic percentage, but not the lab's bands. */
const LOW_RANGE_MOSAIC_MAXIMUM = 50;

export type MosaicRange = "low" | "high";

export type PolygenicRiskCondition = {
  condition: string;
  category: string;
};

/** Whether a report contains the chromosome-testing portion usually called PGT-A. */
export function hasPgtAResult(testType: EmbryoReport["testType"]): boolean {
  return PGT_A_TEST_TYPES.has(testType);
}

/**
 * A precise mosaic percentage is what the API has today. Until the lab also
 * provides its reporting bands, present its range with the conventional 50%
 * split and preserve the exact percentage alongside it.
 */
export function getMosaicRange(level: number): MosaicRange {
  return level <= LOW_RANGE_MOSAIC_MAXIMUM ? "low" : "high";
}

/**
 * The condition catalogue across a set of embryos, ordered by the category a
 * lab supplied and then by condition. Both the page comparison and the detail
 * sheet use this so a condition means the same thing in each place.
 */
export function getPolygenicRiskConditions(
  embryos: readonly Pick<EmbryoGenomicResult, "polygenicRisks">[],
): PolygenicRiskCondition[] {
  const conditions = new Map<string, PolygenicRiskCondition>();
  for (const embryo of embryos) {
    for (const risk of embryo.polygenicRisks) {
      conditions.set(risk.condition, {
        condition: risk.condition,
        category: risk.category,
      });
    }
  }
  return [...conditions.values()].sort(
    (left, right) =>
      left.category.localeCompare(right.category) ||
      left.condition.localeCompare(right.condition),
  );
}

export function getEmbryoTestingOverview(
  report: Pick<EmbryoReport, "reportState" | "testType" | "embryos">,
  untested: number,
): EmbryoTestingOverview {
  const counts = countEmbryoResults(report.embryos);
  const tested = report.embryos.length;
  const state: EmbryoTestingState =
    report.reportState === "retracted"
      ? "withdrawn"
      : tested === 0 && UNTESTED_REPORT_TYPES.has(report.testType)
        ? "not_tested"
        : tested === 0 || report.reportState === "pending"
          ? "awaiting_results"
          : "results";

  return {
    state,
    isPartial: report.reportState === "partial",
    tested,
    untested,
    counts,
    hasNoTransferableResult:
      tested > 0 &&
      counts.euploid === 0 &&
      counts.mosaic === 0 &&
      counts.no_result < tested,
    hasOnlyNoResults: tested > 0 && counts.no_result === tested,
  };
}

// ── Why each embryo got its result ──

export type EmbryoResultReason =
  | { kind: "extra_copy"; chromosome: string }
  | { kind: "missing_copy"; chromosome: string }
  | { kind: "changes_on"; chromosomes: string[] }
  | { kind: "mosaic"; chromosome: string; level: number }
  | { kind: "mosaic_on"; chromosomes: string[] }
  | { kind: "no_result" };

function expectedCopies(chromosome: string, sex: EmbryoGenomicResult["sex"]) {
  if (chromosome === "X") return sex === "XX" ? 2 : 1;
  if (chromosome === "Y") return sex === "XX" ? 0 : 1;
  return 2;
}

/**
 * The finding behind a result, in the terms a patient can repeat to their
 * clinic: which chromosome, and whether it was an extra or a missing copy, or
 * how much of the embryo was mosaic. Euploid embryos need no reason.
 */
export function getEmbryoResultReason(
  embryo: Pick<
    EmbryoGenomicResult,
    "result" | "affectedChromosomes" | "mosaicism" | "cnProfile" | "sex"
  >,
): EmbryoResultReason | null {
  if (embryo.result === "no_result") return { kind: "no_result" };

  if (embryo.result === "mosaic") {
    const mosaicism = embryo.mosaicism ?? [];
    if (mosaicism.length === 1 && mosaicism[0]) {
      return {
        kind: "mosaic",
        chromosome: mosaicism[0].chromosome,
        level: Math.round(mosaicism[0].level),
      };
    }
    if (mosaicism.length > 1) {
      return {
        kind: "mosaic_on",
        chromosomes: mosaicism.map((m) => m.chromosome),
      };
    }
    return null;
  }

  if (embryo.result === "aneuploid") {
    const affected = embryo.affectedChromosomes ?? [];
    if (affected.length === 1 && affected[0]) {
      const chromosome = affected[0];
      const point = embryo.cnProfile?.find((p) => p.chr === chromosome);
      const expected = expectedCopies(chromosome, embryo.sex);
      if (point && point.cn > expected) {
        return { kind: "extra_copy", chromosome };
      }
      if (point && point.cn < expected) {
        return { kind: "missing_copy", chromosome };
      }
    }
    return affected.length > 0
      ? { kind: "changes_on", chromosomes: affected }
      : null;
  }

  return null;
}

// ── Every embryo from the cycle ──

export type EmbryoEntry =
  | {
      kind: "tested";
      key: string;
      embryo: EmbryoGenomicResult;
      storage: EmbryoStorageState | null;
    }
  | {
      /** Biopsied, with the result still to come from the lab. */
      kind: "awaiting";
      key: string;
      /** 1-based position among this cycle's embryos awaiting a result. */
      number: number;
      specimen: Specimen;
      grade: string | null;
      day: number | null;
      storage: EmbryoStorageState | null;
    }
  | {
      kind: "untested";
      key: string;
      /** 1-based position among this cycle's untested embryos. */
      number: number;
      specimen: Specimen;
      grade: string | null;
      day: number | null;
      storage: EmbryoStorageState | null;
    };

function latestDay(specimen: Specimen) {
  return [...(specimen.dayDetails ?? [])].sort((a, b) => b.day - a.day)[0];
}

function wasBiopsied(specimen: Specimen): boolean {
  return specimen.specimenUsages.some(
    (usage) => usage.usage === "biopsy" || Boolean(usage.embryoBiopsy),
  );
}

/**
 * The tested embryos in the lab's numbering, then the biopsied embryos still
 * waiting for a result, then the embryos this cycle froze without a biopsy.
 * Tested embryos read their status from the vault record the report links to;
 * the others come from the vault alone.
 *
 * An embryo only counts as waiting while the report is pending or partial, and
 * only when every embryo on the report is linked to its vault record, so a
 * tested embryo the report forgot to link is never listed twice.
 */
export function getCycleEmbryoEntries(
  report: Pick<EmbryoReport, "cycleId" | "embryos" | "reportState">,
  specimens: readonly Specimen[],
): EmbryoEntry[] {
  const specimensById = new Map(specimens.map((s) => [s.id, s]));
  const linkedIds = new Set(
    report.embryos
      .map((embryo) => embryo.embryoSpecimenId)
      .filter((id): id is string => Boolean(id)),
  );

  const tested: EmbryoEntry[] = [...report.embryos]
    .sort((a, b) => a.embryoNumber - b.embryoNumber)
    .map((embryo) => {
      const specimen = embryo.embryoSpecimenId
        ? specimensById.get(embryo.embryoSpecimenId)
        : undefined;
      return {
        kind: "tested",
        key: embryo.id,
        embryo,
        storage: specimen ? getEmbryoStorageState(specimen) : null,
      };
    });

  const resultsStillComing =
    (report.reportState === "pending" || report.reportState === "partial") &&
    report.embryos.every((embryo) => embryo.embryoSpecimenId);
  const awaiting: EmbryoEntry[] = resultsStillComing
    ? specimens
        .filter(
          (specimen) =>
            specimen.type === "embryo" &&
            specimen.cycleId === report.cycleId &&
            !linkedIds.has(specimen.id) &&
            wasBiopsied(specimen),
        )
        .sort((a, b) => a.collectedAt.localeCompare(b.collectedAt))
        .map((specimen, index) => {
          const latest = latestDay(specimen);
          return {
            kind: "awaiting",
            key: specimen.id,
            number: index + 1,
            specimen,
            grade: latest ? formatEmbryoGrade(latest) : null,
            day: latest?.day ?? null,
            storage: getEmbryoStorageState(specimen),
          };
        })
    : [];

  const untested: EmbryoEntry[] = specimens
    .filter(
      (specimen) =>
        specimen.type === "embryo" &&
        specimen.cycleId === report.cycleId &&
        !linkedIds.has(specimen.id) &&
        !wasBiopsied(specimen),
    )
    .sort((a, b) => a.collectedAt.localeCompare(b.collectedAt))
    .map((specimen, index) => {
      const latest = latestDay(specimen);
      return {
        kind: "untested",
        key: specimen.id,
        number: index + 1,
        specimen,
        grade: latest ? formatEmbryoGrade(latest) : null,
        day: latest?.day ?? null,
        storage: getEmbryoStorageState(specimen),
      };
    });

  return [...tested, ...awaiting, ...untested];
}

// ── Embryos across every cycle ──

function isFrozenEuploidInVault(specimen: Specimen): boolean {
  return (
    specimen.type === "embryo" &&
    isInFrozenStorage(specimen) &&
    specimen.specimenUsages.some(
      (usage) =>
        usage.embryoBiopsy?.euploidyStatus === "euploid" &&
        !usage.embryoBiopsy.hasMosaicism,
    )
  );
}

/**
 * Frozen euploid embryos from this report next to every frozen euploid embryo
 * in the vault, for patients banking embryos over several batches. An
 * embryo counts from this report when the report says euploid. Any other
 * embryo counts when another retrieval's report says euploid, or, with no
 * report behind it, when the vault's own biopsy record does. The total is the
 * same whichever retrieval is on screen.
 */
export function countFrozenEuploidEmbryos(
  report: Pick<EmbryoReport, "embryos">,
  specimens: readonly Specimen[],
  otherReports: readonly Pick<EmbryoReport, "embryos">[] = [],
): { fromThisReport: number; total: number } {
  const specimensById = new Map(specimens.map((s) => [s.id, s]));
  const reportResults = new Map(
    report.embryos
      .filter((embryo) => embryo.embryoSpecimenId)
      .map((embryo) => [embryo.embryoSpecimenId as string, embryo.result]),
  );
  const otherReportResults = new Map(
    otherReports.flatMap((other) =>
      other.embryos
        .filter((embryo) => embryo.embryoSpecimenId)
        .map((embryo) => [embryo.embryoSpecimenId as string, embryo.result]),
    ),
  );

  let fromThisReport = 0;
  for (const embryo of report.embryos) {
    const specimen = embryo.embryoSpecimenId
      ? specimensById.get(embryo.embryoSpecimenId)
      : undefined;
    if (
      embryo.result === "euploid" &&
      specimen &&
      isInFrozenStorage(specimen)
    ) {
      fromThisReport += 1;
    }
  }

  const fromOtherRecords = specimens.filter((specimen) => {
    if (reportResults.has(specimen.id)) return false;
    const otherResult = otherReportResults.get(specimen.id);
    if (otherResult) {
      return (
        otherResult === "euploid" &&
        specimen.type === "embryo" &&
        isInFrozenStorage(specimen)
      );
    }
    return isFrozenEuploidInVault(specimen);
  }).length;

  return { fromThisReport, total: fromThisReport + fromOtherRecords };
}

// ── From retrieval to tested embryos ──

type LabPhase = Pick<Phase, "fertilityMetrics" | "inseminationInfo">;

export type EmbryoLabStageId =
  | "retrieved"
  | "mature"
  | "injected"
  | "fertilized"
  | "day3"
  | "blastocysts"
  | "tested"
  | "euploid";

export type EmbryoLabStage = {
  id: EmbryoLabStageId;
  value: number;
  /** Share of the retrieved eggs, or null for the first stage. */
  shareOfRetrieved: number | null;
};

function sumMetric(phases: readonly LabPhase[], type: string): number | null {
  let total: number | null = null;
  for (const phase of phases) {
    for (const metric of phase.fertilityMetrics ?? []) {
      if (metric.type === type && typeof metric.value === "number") {
        total = (total ?? 0) + metric.value;
      }
    }
  }
  return total;
}

/**
 * The path from the eggs retrieved to the embryos tested and found euploid,
 * from the cycle's lab counts and the report. Empty unless the clinic recorded
 * how many eggs were retrieved, so a report without lab counts shows no funnel
 * rather than one that starts halfway.
 */
export function getEmbryoLabStages(
  phases: readonly LabPhase[],
  cycleType: string | null | undefined,
  report: Pick<EmbryoReport, "embryos">,
): EmbryoLabStage[] {
  const retrieved = sumMetric(phases, "retrieved_oocytes");
  if (retrieved == null || retrieved <= 0) return [];

  const usedIcsi =
    cycleType === "icsi" ||
    phases.some((phase) => phase.inseminationInfo?.technique === "icsi");
  const candidates: [EmbryoLabStageId, number | null][] = [
    ["retrieved", retrieved],
    ["mature", sumMetric(phases, "m2_count")],
    ["injected", usedIcsi ? sumMetric(phases, "injected_oocytes") : null],
    ["fertilized", sumMetric(phases, "fertilized_oocytes")],
    ["day3", sumMetric(phases, "embryos_day_3")],
    ["blastocysts", sumMetric(phases, "embryos_day_5")],
    ["tested", report.embryos.length > 0 ? report.embryos.length : null],
    [
      "euploid",
      report.embryos.length > 0
        ? countEmbryoResults(report.embryos).euploid
        : null,
    ],
  ];

  return candidates
    .filter((stage): stage is [EmbryoLabStageId, number] => stage[1] != null)
    .map(([id, value], index) => ({
      id,
      value,
      shareOfRetrieved:
        index === 0 ? null : Math.round((value / retrieved) * 100),
    }));
}

/** How this cycle's eggs were fertilized, when the lab recorded it. */
export function getFertilizationTechnique(
  phases: readonly LabPhase[],
  cycleType: string | null | undefined,
): "ivf" | "icsi" | null {
  if (cycleType === "icsi") return "icsi";
  const recorded = phases
    .map((phase) => phase.inseminationInfo?.technique)
    .find((technique): technique is "ivf" | "icsi" => Boolean(technique));
  return recorded ?? null;
}

// ── What changed between report versions ──

export type EmbryoResultChange = {
  embryoNumber: number;
  from: EmbryoResult | null;
  to: EmbryoResult | null;
};

/**
 * Embryos whose result differs between two versions of a report. Embryo ids
 * are per version, so embryos are matched on the lab's sample id, then on the
 * embryo number.
 */
export function getEmbryoResultChanges(
  previous: Pick<EmbryoReport, "embryos">,
  current: Pick<EmbryoReport, "embryos">,
): EmbryoResultChange[] {
  const keyOf = (embryo: EmbryoGenomicResult) =>
    embryo.sampleId || `#${embryo.embryoNumber}`;
  const before = new Map(previous.embryos.map((e) => [keyOf(e), e]));
  const after = new Map(current.embryos.map((e) => [keyOf(e), e]));
  const keys = new Set([...before.keys(), ...after.keys()]);

  const changes: EmbryoResultChange[] = [];
  for (const key of keys) {
    const was = before.get(key);
    const now = after.get(key);
    if (was?.result === now?.result) continue;
    changes.push({
      embryoNumber: (now ?? was)?.embryoNumber ?? 0,
      from: was?.result ?? null,
      to: now?.result ?? null,
    });
  }
  return changes.sort((a, b) => a.embryoNumber - b.embryoNumber);
}

// ── One embryo's history ──

export type EmbryoHistoryEvent =
  | { kind: "retrieved"; date: string }
  | { kind: "created"; date: string }
  | { kind: "biopsied"; date: string }
  | { kind: "frozen"; date: string }
  | { kind: "reported"; date: string; result: EmbryoResult }
  | { kind: "thawed"; date: string }
  | { kind: "transferred"; date: string; cycleId: string | null };

/**
 * What happened to an embryo from retrieval to today, from the report and its
 * cryo vault record, in date order. The eggs' retrieval comes from the report.
 * The embryo's own creation comes from its vault record, and only shows when
 * it was a different day, as when frozen or donor eggs were fertilized later.
 */
export function getEmbryoHistory(
  report: Pick<EmbryoReport, "retrievalDate" | "reportDate">,
  embryo: Pick<EmbryoGenomicResult, "biopsyDate" | "result">,
  specimen: Specimen | null,
): EmbryoHistoryEvent[] {
  const events: EmbryoHistoryEvent[] = [];
  const retrievedOn = report.retrievalDate?.slice(0, 10);
  const createdOn = specimen?.collectedAt?.slice(0, 10);
  if (retrievedOn) events.push({ kind: "retrieved", date: retrievedOn });
  if (createdOn && createdOn !== retrievedOn) {
    events.push({ kind: "created", date: createdOn });
  }
  if (embryo.biopsyDate) {
    events.push({ kind: "biopsied", date: embryo.biopsyDate });
  }
  for (const usage of specimen?.specimenUsages ?? []) {
    if (!usage.date) continue;
    if (usage.usage === "freeze") {
      events.push({ kind: "frozen", date: usage.date });
    } else if (usage.usage === "thaw") {
      events.push({ kind: "thawed", date: usage.date });
    } else if (usage.usage === "transfer") {
      events.push({
        kind: "transferred",
        date: usage.date,
        cycleId: usage.cycleId ?? null,
      });
    }
  }
  if (report.reportDate) {
    events.push({
      kind: "reported",
      date: report.reportDate,
      result: embryo.result,
    });
  }
  return events.sort((a, b) =>
    a.date.slice(0, 10).localeCompare(b.date.slice(0, 10)),
  );
}
